import React, { useState } from 'react';
import { t } from '../i18n';
import { zoneSelectionNeeded, normalizeZoneOption } from '../lib/zoneSelection';

/**
 * Zone selection screen for lightshow participants.
 * Sector and row are independent — either, both, or neither can be enabled.
 *
 * Props:
 *   zoneConfig - { sectorEnabled, inputType, options, min, max, label, rowEnabled, rowLabel, rowMin, rowMax }
 *   onSelect({ zoneIndex, rowIndex }) - called on each selection change
 *   selectedIndex - currently selected sector index (null if none)
 *   selectedRow - currently selected row number (null if none)
 */
export default function ZoneSelector({ zoneConfig, onSelect, selectedIndex, selectedRow }) {
  const [numberValue, setNumberValue] = useState('');
  const [rowValue, setRowValue] = useState('');

  // Determine what's enabled. A plain option list (sectorEnabled explicitly
  // false, options present) still renders the sector picker — keying hasSector
  // only off sectorEnabled hid the options and showed just "waiting for show".
  // Shared with App.jsx so both decide identically (see zoneSelectionNeeded).
  const { hasSector, hasRow } = zoneSelectionNeeded(zoneConfig);
  const sectorInputType = zoneConfig.inputType || 'select';

  const handleSectorSelect = (index) => {
    onSelect({ zoneIndex: index, rowIndex: selectedRow });
  };

  const handleRowConfirm = () => {
    const row = Number(rowValue);
    const min = zoneConfig.rowMin ?? 1;
    const max = zoneConfig.rowMax ?? 30;
    if (row >= min && row <= max) {
      onSelect({ zoneIndex: selectedIndex, rowIndex: row });
    }
  };

  const isReady =
    (!hasSector || selectedIndex !== null) &&
    (!hasRow || selectedRow !== null);

  return (
    <div style={screenStyle}>
      {/* Sector selection */}
      {hasSector && (
        <div style={sectionStyle}>
          <div style={labelStyle}>{zoneConfig.label || t('selectSector')}</div>

          {sectorInputType === 'select' ? (
            <div style={optionsContainerStyle}>
              {(zoneConfig.options || []).map((option, index) => {
                // #323: an option may be a plain string or a structured label
                // with a main caption + a description ("Unterrang" / "Reihe 1–20:
                // Sektoren A–T"). Selection stays by index.
                const { caption, description } = normalizeZoneOption(option);
                const isSelected = selectedIndex === index;
                return (
                  <button
                    key={index}
                    style={isSelected ? selectedButtonStyle : optionButtonStyle}
                    onClick={() => handleSectorSelect(index)}
                  >
                    <span style={captionTextStyle}>
                      {caption}
                      {isSelected ? ' ✓' : ''}
                    </span>
                    {description ? (
                      <span style={descriptionTextStyle}>{description}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
              <input
                type="number"
                min={zoneConfig.min ?? 0}
                max={zoneConfig.max ?? 9}
                value={numberValue}
                onChange={(e) => setNumberValue(e.target.value)}
                style={numberInputStyle}
                autoFocus={!hasRow}
              />
              <button
                style={selectedIndex !== null ? selectedButtonStyle : confirmButtonStyle}
                disabled={numberValue === '' || Number(numberValue) < (zoneConfig.min ?? 0) || Number(numberValue) > (zoneConfig.max ?? 9)}
                onClick={() => handleSectorSelect(Number(numberValue))}
              >
                {selectedIndex !== null ? t('change') : t('confirm')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Row selection */}
      {hasRow && (
        <div style={sectionStyle}>
          <div style={labelStyle}>{zoneConfig.rowLabel || t('enterRow')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
            <input
              type="number"
              min={zoneConfig.rowMin ?? 1}
              max={zoneConfig.rowMax ?? 30}
              value={rowValue}
              onChange={(e) => setRowValue(e.target.value)}
              style={numberInputStyle}
              autoFocus={!hasSector}
            />
            <button
              style={selectedRow !== null ? selectedButtonStyle : confirmButtonStyle}
              disabled={rowValue === '' || Number(rowValue) < (zoneConfig.rowMin ?? 1) || Number(rowValue) > (zoneConfig.rowMax ?? 30)}
              onClick={handleRowConfirm}
            >
              {selectedRow !== null ? t('change') : t('confirm')}
            </button>
          </div>
        </div>
      )}

      {/* Status */}
      {isReady && (
        <div style={joiningStyle}>{t('waitingForShow')}</div>
      )}
    </div>
  );
}

const screenStyle = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#000',
  color: '#fff',
  padding: '1rem',
  gap: '1rem',
  fontFamily: 'Work Sans, sans-serif',
};

const sectionStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  maxWidth: '320px',
};

const labelStyle = {
  fontSize: '1rem',
  fontWeight: 400,
  marginBottom: '0.5rem',
  textAlign: 'center',
  fontFamily: 'Work Sans, sans-serif',
};

const optionsContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  width: '100%',
};

const optionButtonStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '2px',
  padding: '0.75rem 1.5rem',
  fontSize: '1rem',
  fontWeight: 500,
  background: 'rgba(255, 255, 255, 0.08)',
  color: '#fff',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'background 0.2s, border-color 0.2s, color 0.2s',
  fontFamily: 'Work Sans, sans-serif',
};

// #323: the main sector caption (bold) + its smaller, dimmer description line.
const captionTextStyle = {
  fontWeight: 600,
  fontSize: '1rem',
  lineHeight: 1.2,
};

const descriptionTextStyle = {
  fontWeight: 400,
  fontSize: '0.8rem',
  opacity: 0.7,
  lineHeight: 1.2,
};

const selectedButtonStyle = {
  ...optionButtonStyle,
  background: 'rgba(126, 211, 33, 0.15)',
  borderColor: '#7ed321',
  color: '#7ed321',
};

const joiningStyle = {
  marginTop: '1rem',
  fontSize: '1rem',
  opacity: 0.6,
  fontFamily: 'Work Sans, sans-serif',
};

const numberInputStyle = {
  width: '100px',
  padding: '0.5rem',
  fontSize: '1.25rem',
  textAlign: 'center',
  background: 'rgba(255, 255, 255, 0.08)',
  color: '#fff',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '8px',
  outline: 'none',
  fontFamily: 'Work Sans, sans-serif',
  transition: 'border-color 0.2s, background 0.2s',
};

const confirmButtonStyle = {
  padding: '0.5rem 1.25rem',
  fontSize: '0.95rem',
  fontWeight: 500,
  background: 'rgba(126, 211, 33, 0.15)',
  color: '#7ed321',
  border: '1px solid #7ed321',
  borderRadius: '8px',
  cursor: 'pointer',
  fontFamily: 'Work Sans, sans-serif',
  transition: 'background 0.2s, color 0.2s',
};
